import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  ShoppingCart, 
  ArrowUpRight, 
  ArrowDownRight, 
  Calendar, 
  DollarSign, 
  BarChart2, 
  PieChart, 
  Filter, 
  RefreshCw,
  CreditCard,
  Clock,
  Eye,
  CheckCircle,
  AlertTriangle,
  Smile,
  MessageSquareWarning,
  FileText,
  Package,
  Building2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import MetricCard from '../components/ui/MetricCard';
import SLAGauge from '../components/ui/SLAGauge';
import { useAppStore } from '../store';
import { useExpressCosts } from '../hooks/useExpressCosts';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import {
  totalAccountsReceivableARS,
  totalAccountsOverdueARS,
  clientAccountsReceivableARS,
} from '../utils/accountsReceivable';
import { formatArgentinaDate } from '../utils/dateUtils';
import SalesDetailModal from '../components/modals/SalesDetailModal';
import PurchasesDetailModal from '../components/modals/PurchasesDetailModal';
import ReceivablesDetailModal from '../components/modals/ReceivablesDetailModal';
import PaymentReceiptsCard from '../components/dashboard/PaymentReceiptsCard';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  PointElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { 
  subMonths,
  addMonths,
  subQuarters, 
  subYears, 
  startOfMonth, 
  startOfQuarter, 
  startOfYear, 
  isAfter,
  format,
  endOfMonth,
  endOfQuarter,
  endOfYear
} from 'date-fns';
import { es } from 'date-fns/locale';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// ── helpers Express ───────────────────────────────────────────
const getExpressAmount = (o: any) => o.express_value ?? o.total ?? 0;
const getExpressSampleAmount = (s: any) => s.sample_value ?? s.total ?? 0;
const toDateKey = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  return format(value, 'yyyy-MM-dd');
};

const isInDateRange = (
  value: string | Date | null | undefined,
  start: Date,
  end: Date
): boolean => {
  const key = toDateKey(value);
  if (!key) return false;
  return key >= format(start, 'yyyy-MM-dd') && key <= format(end, 'yyyy-MM-dd');
};

// ── helpers para cálculo sin IVA ──────────────────────────────
// Calcula el monto sin IVA para facturas tipo "A", convirtiendo USD a ARS
const getInvoiceAmountWithoutVAT = (invoice: any) => {
  let amount = 0;
  
  if (invoice.type === 'A') {
    // Para facturas tipo A, usar subtotal (sin IVA)
    amount = invoice.subtotal || 0;
  } else {
    // Para otros tipos, usar total (con IVA)
    amount = invoice.total || 0;
  }
  
  // Si la factura está en USD, convertir a ARS usando el tipo de cambio
  if (invoice.currency === 'USD' && invoice.exchange_rate) {
    amount = amount * invoice.exchange_rate;
  }
  
  return amount;
};

// Calcula el monto sin IVA para gastos
const getExpenseAmountWithoutVAT = (expense: any) => {
  // Para gastos, siempre usar el monto sin IVA
  return (expense.amount || 0) - (expense.vat_amount || 0);
};

// Convierte el monto de un pago a ARS si está en USD
const getPaymentAmountInARS = (payment: any) => {
  let amount = payment.amount || 0;
  
  // Si el pago está en USD, convertir a ARS
  if (payment.currency === 'USD' && payment.exchange_rate) {
    amount = amount * payment.exchange_rate;
  }
  
  return amount;
};

// ──────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { 
    invoices, 
    payments, 
    clients, 
    suppliers, 
    getEntityBalanceByCurrency,
    productions,
    productionTasks,
    samples,
    sampleTasks,
    express,
    expressSamples,
    expenses,
    darkMode 
  } = useAppStore();
  
  const { costs: expressCosts } = useExpressCosts();
  
  type PeriodMode = 'month' | 'quarter' | 'year' | 'custom';
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
  /** Primer día del mes en vista (modo mes). Nunca se usa como rango custom. */
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isSalesDetailModalOpen, setIsSalesDetailModalOpen] = useState(false);
  const [isPurchasesDetailModalOpen, setIsPurchasesDetailModalOpen] = useState(false);
  const [isReceivablesDetailModalOpen, setIsReceivablesDetailModalOpen] = useState(false);
  const [satisfactionIndex, setSatisfactionIndex] = useState<number>(0);
  const [openComplaintsCount, setOpenComplaintsCount] = useState<number>(0);

  const parseLocalDate = (value: string): Date => {
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return startOfMonth(new Date());
    return new Date(y, m - 1, d);
  };

  // Fetch satisfaction and claims for real metrics
  useEffect(() => {
    const loadSatisfactionAndClaims = async () => {
      try {
        const [surveysRes, responsesRes, claimsRes] = await Promise.all([
          supabase.from('satisfaction_surveys').select('id, status').eq('status', 'completed'),
          supabase.from('satisfaction_survey_responses').select('survey_id, q1_experience_rating, q2_quality_rating, q3_delivery_rating, q4_communication_rating'),
          supabase.from('claims').select('id, status').eq('status', 'pending'),
        ]);
        if (surveysRes.error) throw surveysRes.error;
        if (responsesRes.error) throw responsesRes.error;
        if (claimsRes.error) throw claimsRes.error;

        const surveys = surveysRes.data || [];
        const responses = responsesRes.data || [];
        const respMap = new Map(responses.map((r: any) => [r.survey_id, r]));

        const indices: number[] = [];
        surveys.forEach((s: any) => {
          const r = respMap.get(s.id);
          if (!r) return;
          const ratings = [r.q1_experience_rating, r.q2_quality_rating, r.q3_delivery_rating, r.q4_communication_rating]
            .filter((v): v is number => v != null && v >= 1 && v <= 5);
          if (ratings.length > 0) {
            indices.push(ratings.reduce((a, b) => a + b, 0) / ratings.length);
          }
        });
        const avg = indices.length > 0 ? indices.reduce((a, b) => a + b, 0) / indices.length : 0;
        setSatisfactionIndex(Math.round((avg / 5) * 100));

        setOpenComplaintsCount((claimsRes.data || []).length);
      } catch (e) {
        console.error('Error loading satisfaction/claims:', e);
      }
    };
    loadSatisfactionAndClaims();
  }, []);

  const currentMonthStart = startOfMonth(new Date());

  // Rango del período actual (meses calendario reales en modo mes)
  const periodStartDate = useMemo(() => {
    const now = new Date();
    switch (periodMode) {
      case 'month':
        return startOfMonth(viewMonth);
      case 'quarter':
        return startOfQuarter(now);
      case 'year':
        return startOfYear(now);
      case 'custom':
        return startDate ? parseLocalDate(startDate) : startOfMonth(now);
      default:
        return startOfMonth(viewMonth);
    }
  }, [periodMode, viewMonth, startDate]);

  const periodEndDate = useMemo(() => {
    const now = new Date();
    switch (periodMode) {
      case 'month':
        return endOfMonth(viewMonth);
      case 'quarter':
        return endOfQuarter(now);
      case 'year':
        return endOfYear(now);
      case 'custom':
        return endDate ? parseLocalDate(endDate) : endOfMonth(now);
      default:
        return endOfMonth(viewMonth);
    }
  }, [periodMode, viewMonth, endDate]);

  // Período anterior comparable (mes anterior, trimestre anterior, etc.)
  const previousPeriodStartDate = useMemo(() => {
    switch (periodMode) {
      case 'month':
        return startOfMonth(subMonths(viewMonth, 1));
      case 'quarter':
        return startOfQuarter(subQuarters(new Date(), 1));
      case 'year':
        return startOfYear(subYears(new Date(), 1));
      case 'custom': {
        const durationMs = Math.max(0, periodEndDate.getTime() - periodStartDate.getTime());
        const prevEnd = new Date(periodStartDate.getTime() - 1);
        return new Date(prevEnd.getTime() - durationMs);
      }
      default:
        return startOfMonth(subMonths(viewMonth, 1));
    }
  }, [periodMode, viewMonth, periodStartDate, periodEndDate]);

  const previousPeriodEndDate = useMemo(() => {
    switch (periodMode) {
      case 'month':
        return endOfMonth(subMonths(viewMonth, 1));
      case 'quarter':
        return endOfQuarter(subQuarters(new Date(), 1));
      case 'year':
        return endOfYear(subYears(new Date(), 1));
      case 'custom':
        return new Date(periodStartDate.getTime() - 1);
      default:
        return endOfMonth(subMonths(viewMonth, 1));
    }
  }, [periodMode, viewMonth, periodStartDate]);

  // Sync date inputs when not in custom mode
  useEffect(() => {
    if (periodMode !== 'custom') {
      setStartDate(format(periodStartDate, 'yyyy-MM-dd'));
      setEndDate(format(periodEndDate, 'yyyy-MM-dd'));
    }
  }, [periodMode, periodStartDate, periodEndDate]);

  useEffect(() => { setTransactionsPage(1); }, [periodMode, periodStartDate, periodEndDate]);

  const periodLabel = useMemo(() => {
    if (periodMode === 'month') {
      return format(viewMonth, "LLLL 'de' yyyy", { locale: es }).replace(/^\w/, (c) => c.toUpperCase());
    }
    if (periodMode === 'quarter') {
      return `${format(periodStartDate, 'MMM', { locale: es })} – ${format(periodEndDate, 'MMM', { locale: es })} ${format(periodStartDate, 'yyyy')}`;
    }
    if (periodMode === 'year') {
      return format(periodStartDate, 'yyyy');
    }
    if (startDate && endDate) {
      return `${format(periodStartDate, 'dd/MM/yyyy')} – ${format(periodEndDate, 'dd/MM/yyyy')}`;
    }
    return 'Período personalizado';
  }, [periodMode, viewMonth, periodStartDate, periodEndDate, startDate, endDate]);

  const canGoNextMonth = !isAfter(startOfMonth(addMonths(viewMonth, 1)), currentMonthStart);

  const goToPrevMonth = () => {
    setPeriodMode('month');
    setViewMonth((m) => startOfMonth(subMonths(m, 1)));
  };

  const goToNextMonth = () => {
    const next = startOfMonth(addMonths(viewMonth, 1));
    if (isAfter(next, currentMonthStart)) return;
    setPeriodMode('month');
    setViewMonth(next);
  };

  const handlePeriodModeChange = (value: string) => {
    const mode = value as PeriodMode;
    setPeriodMode(mode);
    if (mode === 'month') {
      setViewMonth(startOfMonth(new Date()));
    }
  };
  
  // Filter invoices by period
  const periodInvoices = useMemo(() => {
    return invoices.filter(invoice =>
      isInDateRange(invoice.date, periodStartDate, periodEndDate) &&
      invoice.entity_type === 'client' &&
      !invoice.voided
    );
  }, [invoices, periodStartDate, periodEndDate]);
  
  // Filter invoices by previous period
  const previousPeriodInvoices = useMemo(() => {
    return invoices.filter(invoice =>
      isInDateRange(invoice.date, previousPeriodStartDate, previousPeriodEndDate) &&
      invoice.entity_type === 'client' &&
      !invoice.voided
    );
  }, [invoices, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter supplier invoices by period
  const periodSupplierInvoices = useMemo(() => {
    return invoices.filter(invoice =>
      isInDateRange(invoice.date, periodStartDate, periodEndDate) &&
      invoice.entity_type === 'supplier' &&
      !invoice.voided
    );
  }, [invoices, periodStartDate, periodEndDate]);
  
  // Filter supplier invoices by previous period
  const previousPeriodSupplierInvoices = useMemo(() => {
    return invoices.filter(invoice =>
      isInDateRange(invoice.date, previousPeriodStartDate, previousPeriodEndDate) &&
      invoice.entity_type === 'supplier' &&
      !invoice.voided
    );
  }, [invoices, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter expenses by period
  const periodExpenses = useMemo(() => {
    return expenses.filter(expense =>
      isInDateRange(expense.date, periodStartDate, periodEndDate)
    );
  }, [expenses, periodStartDate, periodEndDate]);
  
  // Filter expenses by previous period
  const previousPeriodExpenses = useMemo(() => {
    return expenses.filter(expense =>
      isInDateRange(expense.date, previousPeriodStartDate, previousPeriodEndDate)
    );
  }, [expenses, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter productions by period
  const periodProductions = useMemo(() => {
    return productions.filter(production =>
      isInDateRange(production.created_at, periodStartDate, periodEndDate)
    );
  }, [productions, periodStartDate, periodEndDate]);
  
  // Filter productions by previous period
  const previousPeriodProductions = useMemo(() => {
    return productions.filter(production =>
      isInDateRange(production.created_at, previousPeriodStartDate, previousPeriodEndDate)
    );
  }, [productions, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter samples by period
  const periodSamples = useMemo(() => {
    return samples.filter(sample =>
      isInDateRange(sample.created_at, periodStartDate, periodEndDate)
    );
  }, [samples, periodStartDate, periodEndDate]);
  
  // Filter samples by previous period
  const previousPeriodSamples = useMemo(() => {
    return samples.filter(sample =>
      isInDateRange(sample.created_at, previousPeriodStartDate, previousPeriodEndDate)
    );
  }, [samples, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter production tasks by period (by production start_date, not task created_at)
  const periodProductionTasks = useMemo(() => {
    return productionTasks.filter(task => {
      const production = productions.find(p => p.id === task.production_id);
      if (!production) return false;
      const d = (production as { start_date?: string }).start_date ?? production.created_at;
      return isInDateRange(d, periodStartDate, periodEndDate);
    });
  }, [productionTasks, productions, periodStartDate, periodEndDate]);
  
  // Filter sample tasks by period (by sample start_date, not task created_at)
  const periodSampleTasks = useMemo(() => {
    return sampleTasks.filter(task => {
      const sample = samples.find(s => s.id === task.sample_id);
      if (!sample) return false;
      const d = (sample as { start_date?: string }).start_date ?? sample.created_at;
      return isInDateRange(d, periodStartDate, periodEndDate);
    });
  }, [sampleTasks, samples, periodStartDate, periodEndDate]);
  
const periodExpress = express.filter(o =>
  isInDateRange(o.date ?? o.created_at, periodStartDate, periodEndDate)
);

const previousPeriodExpress = express.filter(o =>
  isInDateRange(o.date ?? o.created_at, previousPeriodStartDate, previousPeriodEndDate)
);

const periodExpressSamples = expressSamples.filter(sp =>
  isInDateRange(sp.date ?? sp.created_at, periodStartDate, periodEndDate)
);

const previousPeriodExpressSamples = expressSamples.filter(sp =>
  isInDateRange(sp.date ?? sp.created_at, previousPeriodStartDate, previousPeriodEndDate)
);

const totalSales =
  periodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0) +
  periodExpress.reduce((s, o) => s + getExpressAmount(o), 0) +
  periodExpressSamples.reduce((s, sp) => s + getExpressSampleAmount(sp), 0) +
  periodSamples.reduce((s, sample) => s + (sample.sample_value || 0), 0);


// --------------------------------------------------------------
  
  // Prepare detailed sales data for the modal
const detailedSalesData = useMemo(() => {
  const invoiceSales       = periodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0);
  const productionSales    = periodProductions.reduce((s, p) => s + (p.production_value || 0), 0);
  const sampleSales        = periodSamples.reduce((s, sp) => s + (sp.sample_value || 0), 0);
  const expressSales       = periodExpress.reduce((s, o) => s + getExpressAmount(o), 0);
  const expressSampleSales = periodExpressSamples.reduce((s, sp) => s + (sp.sample_value || 0), 0);

  return {
    totalSales,
    invoiceSales,
    productionSales,
    sampleSales,
    expressSales,
    expressSampleSales
  };
}, [totalSales, periodInvoices, periodProductions, periodSamples, periodExpress, periodExpressSamples]);

  // Total ventas período anterior (para margen bruto)
  const previousTotalSales = useMemo(() => {
    return previousPeriodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0) +
      previousPeriodExpress.reduce((s, o) => s + getExpressAmount(o), 0) +
      previousPeriodExpressSamples.reduce((s, sp) => s + getExpressSampleAmount(sp), 0) +
      previousPeriodSamples.reduce((s, sample) => s + (sample.sample_value || 0), 0);
  }, [previousPeriodInvoices, previousPeriodExpress, previousPeriodExpressSamples, previousPeriodSamples]);

  // Total facturado del período anterior (para % cambio en card Ventas)
  const previousInvoiceSales = useMemo(() => {
    return previousPeriodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0);
  }, [previousPeriodInvoices]);

  // Porcentaje cambio facturado vs período anterior
  const salesChangePercentage = useMemo(() => {
    const current = detailedSalesData.invoiceSales;
    if (previousInvoiceSales === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previousInvoiceSales) / previousInvoiceSales) * 100);
  }, [detailedSalesData.invoiceSales, previousInvoiceSales]);
  
  // Calculate total purchases for current period (including expenses and task costs)
  const totalPurchases = useMemo(() => {
    const supplierInvoicesTotal = periodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = periodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    
    // Add task costs (productions and samples)
    const productionTaskCosts = periodProductionTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    const sampleTaskCosts = periodSampleTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    
    return supplierInvoicesTotal + expensesTotal + productionTaskCosts + sampleTaskCosts;
  }, [periodSupplierInvoices, periodExpenses, periodProductionTasks, periodSampleTasks]);
  
  // Prepare detailed purchases data for the modal
  const detailedPurchasesData = useMemo(() => {
    const supplierInvoicesTotal = periodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = periodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    
    // Add task costs
    const productionTaskCosts = periodProductionTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    const sampleTaskCosts = periodSampleTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    
    return {
      totalPurchases,
      supplierInvoicesTotal,
      expensesTotal,
      productionTaskCosts,
      sampleTaskCosts
    };
  }, [totalPurchases, periodSupplierInvoices, periodExpenses, periodProductionTasks, periodSampleTasks]);
  
  // Calculate total purchases for previous period (including expenses and task costs)
  const previousTotalPurchases = useMemo(() => {
    const supplierInvoicesTotal = previousPeriodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = previousPeriodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    
    // Add task costs from previous period (by production/sample start_date)
    const prevStartStr = format(previousPeriodStartDate, 'yyyy-MM-dd');
    const prevEndStr = format(previousPeriodEndDate, 'yyyy-MM-dd');
    const previousPeriodProductionTasks = productionTasks.filter(task => {
      const production = productions.find(p => p.id === task.production_id);
      if (!production) return false;
      const d = (production as { start_date?: string }).start_date ?? production.created_at?.slice(0, 10) ?? '';
      const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
      return dStr >= prevStartStr && dStr <= prevEndStr;
    });
    
    const previousPeriodSampleTasks = sampleTasks.filter(task => {
      const sample = samples.find(s => s.id === task.sample_id);
      if (!sample) return false;
      const d = (sample as { start_date?: string }).start_date ?? sample.created_at?.slice(0, 10) ?? '';
      const dStr = typeof d === 'string' ? d.slice(0, 10) : '';
      return dStr >= prevStartStr && dStr <= prevEndStr;
    });
    
    const productionTaskCosts = previousPeriodProductionTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    const sampleTaskCosts = previousPeriodSampleTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
    
    return supplierInvoicesTotal + expensesTotal + productionTaskCosts + sampleTaskCosts;
  }, [previousPeriodSupplierInvoices, previousPeriodExpenses, productionTasks, sampleTasks, productions, samples, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Calculate purchases change percentage
  const purchasesChangePercentage = useMemo(() => {
    if (previousTotalPurchases === 0) return 100;
    return Math.round(((totalPurchases - previousTotalPurchases) / previousTotalPurchases) * 100);
  }, [totalPurchases, previousTotalPurchases]);
  
  // Cuentas por cobrar: suma del equivalente ARS del saldo actual de cada ficha de cliente
  const totalReceivable = useMemo(
    () => totalAccountsReceivableARS(clients, invoices, payments),
    [clients, invoices, payments]
  );

  const totalOverdue = useMemo(
    () => totalAccountsOverdueARS(clients, invoices, payments),
    [clients, invoices, payments]
  );
  
  
  // Calculate gross margin
  const grossMargin = useMemo(() => {
    return totalSales - totalPurchases;
  }, [totalSales, totalPurchases]);
  
  // Calculate previous gross margin
  const previousGrossMargin = useMemo(() => {
    return previousTotalSales - previousTotalPurchases;
  }, [previousTotalSales, previousTotalPurchases]);
  
  // Calculate gross margin change percentage
  const grossMarginChangePercentage = useMemo(() => {
    if (previousGrossMargin === 0) return 100;
    return Math.round(((grossMargin - previousGrossMargin) / Math.abs(previousGrossMargin)) * 100);
  }, [grossMargin, previousGrossMargin]);
  
  // Calculate gross margin percentage
  const grossMarginPercentage = useMemo(() => {
    if (totalSales === 0) return 0;
    return Math.round((grossMargin / totalSales) * 100);
  }, [grossMargin, totalSales]);
  
  // Valor factura promedio (real)
  const averageInvoiceValue = useMemo(() => {
    const total = periodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0);
    return periodInvoices.length > 0 ? total / periodInvoices.length : 0;
  }, [periodInvoices]);

  // Cantidad de facturas y clientes únicos (real)
  const invoiceCount = periodInvoices.length;
  // Proveedores únicos con actividad en el período
  const uniqueSuppliersCount = useMemo(() => {
    const ids = new Set<string>();
    periodSupplierInvoices.forEach(i => { if (i.entity_id) ids.add(i.entity_id); });
    periodProductionTasks.forEach(t => { if (t.supplier_id) ids.add(t.supplier_id); });
    periodSampleTasks.forEach(t => { if (t.supplier_id) ids.add(t.supplier_id); });
    periodExpenses.forEach(e => { if (e.supplier_id) ids.add(e.supplier_id); });
    return ids.size;
  }, [periodSupplierInvoices, periodProductionTasks, periodSampleTasks, periodExpenses]);

  // Operaciones del período (producciones + muestras + express)
  const periodOperationsCount = periodProductions.length + periodSamples.length + periodExpress.length + periodExpressSamples.length;

  // Satisfacción y reclamos se cargan desde Supabase en useEffect

  // Calculate delivery compliance (period-based, for backward compatibility)
  const deliveryCompliance = useMemo(() => {
    const allEntities = [
      ...productions.map(p => ({ ...p, type: 'production' as const })),
      ...samples.map(s => ({ ...s, type: 'sample' as const }))
    ];
    
    const completedEntities = allEntities.filter(entity => {
      const entityTasks = entity.type === 'production' 
        ? productionTasks.filter(t => t.production_id === entity.id)
        : sampleTasks.filter(t => t.sample_id === entity.id);
      return entityTasks.some(task => task.status === 'finished');
    });
    
    if (completedEntities.length === 0) return { percentage: 0, onTime: 0, late: 0, total: 0 };
    
    let onTime = 0;
    let late = 0;
    completedEntities.forEach(entity => {
      const entityTasks = entity.type === 'production' 
        ? productionTasks.filter(t => t.production_id === entity.id)
        : sampleTasks.filter(t => t.sample_id === entity.id);
      const finishedTask = entityTasks.find(t => t.status === 'finished');
      if (finishedTask && finishedTask.end_date && entity.delivery_date) {
        const completionDate = new Date(finishedTask.end_date);
        const deliveryDate = new Date(entity.delivery_date);
        if (completionDate <= deliveryDate) onTime++;
        else late++;
      }
    });
    const total = onTime + late;
    const percentage = total > 0 ? Math.round((onTime / total) * 100) : 0;
    return { percentage, onTime, late, total };
  }, [productions, samples, productionTasks, sampleTasks]);

  // SLA cumplido: producciones y muestras por separado, promedio combinado (60 días fijos)
  const slaCompliance60d = useMemo(() => {
    const now = new Date();
    const startDate = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    const isFinished = (e: { status: string }) =>
      e.status === 'finished' || e.status === 'shipped' || e.status === 'shipped/despatched';
    const calcFor = (entities: Array<{ delivery_date?: string; status: string; finished_at?: string }>) => {
      const filtered = entities.filter(e => e.delivery_date && new Date(e.delivery_date) >= startDate);
      const finished = filtered.filter(e => isFinished(e));
      const onTime = finished.filter(e =>
        e.delivery_date && e.finished_at && new Date(e.finished_at) <= new Date(e.delivery_date)
      );
      const pct = finished.length > 0 ? Math.round((onTime.length / finished.length) * 100) : 0;
      return { percentage: pct, onTime: onTime.length, late: finished.length - onTime.length, total: finished.length };
    };
    const prod = calcFor(productions);
    const samp = calcFor(samples);
    const hasProd = prod.total > 0;
    const hasSamp = samp.total > 0;
    const avgPct = hasProd && hasSamp
      ? Math.round((prod.percentage + samp.percentage) / 2)
      : hasProd ? prod.percentage : hasSamp ? samp.percentage : 0;
    return {
      percentage: avgPct,
      onTime: prod.onTime + samp.onTime,
      late: prod.late + samp.late,
      total: prod.total + samp.total,
      production: prod,
      sample: samp,
    };
  }, [productions, samples]);
  
  // Calculate PRO gross margin (productions + samples - task costs)
  const proGrossMargin = useMemo(() => {
    const productionValue = periodProductions.reduce((sum, p) => sum + (p.production_value || 0), 0);
    const sampleValue = periodSamples.reduce((sum, s) => sum + (s.sample_value || 0), 0);
    
    const productionTaskCosts = periodProductionTasks.reduce((sum, t) => sum + (t.cost || 0), 0);
    const sampleTaskCosts = periodSampleTasks.reduce((sum, t) => sum + (t.cost || 0), 0);
    
    return (productionValue + sampleValue) - (productionTaskCosts + sampleTaskCosts);
  }, [periodProductions, periodSamples, periodProductionTasks, periodSampleTasks]);
  
  // Calculate EXPRESS gross margin (express + express samples - configurable costs)
  const expressGrossMargin = useMemo(() => {
    const expressValue = periodExpress.reduce((sum, o) => sum + getExpressAmount(o), 0);
    const expressSampleValue = periodExpressSamples.reduce((sum, s) => sum + getExpressSampleAmount(s), 0);
    
    // Get costs from settings
    const remeraCost = expressCosts.remera_cost || 0;
    const buzoCost = expressCosts.buzo_cost || 0;
    const estampadoCost = expressCosts.estampado_cost || 0;
    
    // Calculate costs based on express items (this is a simplified calculation)
    // In reality, you'd need to analyze the actual items to determine costs
    // For now, using a percentage-based approach
    const totalValue = expressValue + expressSampleValue;
    const estimatedCosts = totalValue * 0.3; // Placeholder: 30% of value
    
    // You could also calculate based on actual item types if available
    // const itemBasedCosts = expressItems.reduce((sum, item) => {
    //   if (item.type === 'remera') return sum + (remeraCost * item.quantity);
    //   if (item.type === 'buzo') return sum + (buzoCost * item.quantity);
    //   return sum + (estampadoCost * item.quantity);
    // }, 0);
    
    return totalValue - estimatedCosts;
  }, [periodExpress, periodExpressSamples, expressCosts]);
  
  // Prepare data for sales by client chart (remove legend)
  const salesByClientData = useMemo(() => {
    const clientSales: Record<string, number> = {};
    
    // Add invoice sales
    periodInvoices.forEach(invoice => {
      if (invoice.entity_id) {
        const client = clients.find(c => c.id === invoice.entity_id);
        if (client?.name) {
          clientSales[client.name] = (clientSales[client.name] || 0) + getInvoiceAmountWithoutVAT(invoice);
        }
      }
    });

    // Add express sales
    periodExpress.forEach(o => {
      if (o.client_id) {
        const client = clients.find(c => c.id === o.client_id);
        if (client?.name) {
          clientSales[client.name] = (clientSales[client.name] || 0) + getExpressAmount(o);
        }
      }
    });
    
    // Add production sales
    periodProductions.forEach(production => {
      if (production.client_id) {
        const client = clients.find(c => c.id === production.client_id);
        if (client?.name) {
          clientSales[client.name] = (clientSales[client.name] || 0) + (production.production_value || 0);
        }
      }
    });
    
    // Add sample sales
    periodSamples.forEach(sample => {
      if (sample.client_id) {
        const client = clients.find(c => c.id === sample.client_id);
        if (client?.name) {
          clientSales[client.name] = (clientSales[client.name] || 0) + (sample.sample_value || 0);
        }
      }
    });
    
    // Add express sample sales
    periodExpressSamples.forEach(sample => {
      if (sample.client_id) {
        const client = clients.find(c => c.id === sample.client_id);
        if (client?.name) {
          clientSales[client.name] = (clientSales[client.name] || 0) + (sample.sample_value || 0);
        }
      }
    });
    
    // Convert to array and sort by sales amount
    const sortedSales = Object.entries(clientSales)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 clients
    
    return {
      labels: sortedSales.map(([client]) => client),
      datasets: [
        {
          data: sortedSales.map(([, amount]) => amount),
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(79, 70, 229, 0.8)',
            'rgba(67, 56, 202, 0.8)',
            'rgba(55, 48, 163, 0.8)',
            'rgba(49, 46, 129, 0.8)',
          ],
          borderColor: [
            'rgba(99, 102, 241, 1)',
            'rgba(79, 70, 229, 1)',
            'rgba(67, 56, 202, 1)',
            'rgba(55, 48, 163, 1)',
            'rgba(49, 46, 129, 1)',
          ],
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [periodInvoices, periodProductions, periodSamples, periodExpress, periodExpressSamples, clients]);
  
  // Prepare data for purchases by supplier chart (include production tasks)
  const purchasesBySupplierData = useMemo(() => {
    const supplierPurchases: Record<string, number> = {};
    
    // Add supplier invoices
    periodSupplierInvoices.forEach(invoice => {
      if (invoice.entity_id) {
        const supplier = suppliers.find(s => s.id === invoice.entity_id);
        if (supplier?.name) {
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + getInvoiceAmountWithoutVAT(invoice);
        }
      }
    });
    
    // Add production tasks by supplier
    periodProductionTasks.forEach(task => {
      if (task.supplier_id) {
        const supplier = suppliers.find(s => s.id === task.supplier_id);
        if (supplier?.name) {
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + (task.cost || 0);
        }
      }
    });
    
    // Add sample tasks by supplier
    periodSampleTasks.forEach(task => {
      if (task.supplier_id) {
        const supplier = suppliers.find(s => s.id === task.supplier_id);
        if (supplier?.name) {
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + (task.cost || 0);
        }
      }
    });
    
    // Add expenses by supplier
    periodExpenses.forEach(expense => {
      if (expense.supplier_id) {
        const supplier = suppliers.find(s => s.id === expense.supplier_id);
        if (supplier?.name) {
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + getExpenseAmountWithoutVAT(expense);
        } else {
          // For expenses without supplier, group as "Otros gastos"
          supplierPurchases["Otros gastos"] = (supplierPurchases["Otros gastos"] || 0) + getExpenseAmountWithoutVAT(expense);
        }
      } else {
        // For expenses without supplier, group as "Otros gastos"
        supplierPurchases["Otros gastos"] = (supplierPurchases["Otros gastos"] || 0) + getExpenseAmountWithoutVAT(expense);
      }
    });
    
    // Convert to array and sort by purchase amount
    const sortedPurchases = Object.entries(supplierPurchases)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 suppliers
    
    return {
      labels: sortedPurchases.map(([supplier]) => supplier),
      datasets: [
        {
          label: 'Compras',
          data: sortedPurchases.map(([, amount]) => amount),
          backgroundColor: [
            'rgba(239, 68, 68, 0.8)',
            'rgba(249, 115, 22, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(234, 179, 8, 0.8)',
            'rgba(132, 204, 22, 0.8)',
          ],
          borderColor: [
            'rgba(239, 68, 68, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(234, 179, 8, 1)',
            'rgba(132, 204, 22, 1)',
          ],
          borderWidth: 1,
          hoverOffset: 15,
        },
      ],
    };
  }, [periodSupplierInvoices, periodProductionTasks, periodSampleTasks, periodExpenses, suppliers]);
  
  // Top cuentas por cobrar: mismo criterio que totalReceivable (saldo actual por cliente en ARS eq.)
  const topAccountsReceivable = useMemo(() => {
    return clients
      .map((client) => ({
        ...client,
        balanceInARS: clientAccountsReceivableARS(client.id, invoices, payments),
      }))
      .filter((c) => c.balanceInARS > 0)
      .sort((a, b) => b.balanceInARS - a.balanceInARS)
      .slice(0, 5);
  }, [clients, invoices, payments]);
  
  const TRANSACTIONS_PER_PAGE = 15;
  const [transactionsPage, setTransactionsPage] = useState(1);
  
  const recentTransactionsAll = useMemo(() => {
    const getPaymentEntity = (pay: any) => {
      const inv = invoices.find(i => i.id === pay.invoice_id);
      if (inv) {
        return inv.entity_type === 'client'
          ? clients.find(c => c.id === inv.entity_id)?.name || 'Cliente desconocido'
          : suppliers.find(s => s.id === inv.entity_id)?.name || 'Proveedor desconocido';
      }
      if (pay.reference) {
        const [type, id] = pay.reference.split(':');
        if (type === 'client' && id) return clients.find(c => c.id === id)?.name || 'Cliente';
        if (type === 'supplier' && id) return suppliers.find(s => s.id === id)?.name || 'Proveedor';
      }
      return 'Pago a cuenta';
    };

    const all = [
      ...invoices.filter(inv => !inv.voided).map(inv => ({
        id: inv.id,
        date: inv.date,
        timestamp: new Date(inv.date).getTime(),
        createdAt: new Date(inv.created_at).getTime(),
        description: `Factura ${inv.number}`,
        entity: inv.entity_type === 'client'
          ? clients.find(c => c.id === inv.entity_id)?.name || 'Cliente desconocido'
          : suppliers.find(s => s.id === inv.entity_id)?.name || 'Proveedor desconocido',
        amount: getInvoiceAmountWithoutVAT(inv),
        type: inv.entity_type === 'client' ? 'income' as const : 'expense' as const
      })),
      ...payments.filter(p => !p.voided).map(pay => {
        const inv = invoices.find(i => i.id === pay.invoice_id);
        const isCreditNote = (pay as any).invoice_type === 'credit_note';
        const payType = isCreditNote ? 'credit_note' : (inv?.entity_type === 'client' ? 'payment_received' : 'payment_made');
        return {
          id: pay.id,
          date: pay.date,
          timestamp: new Date(pay.date).getTime(),
          createdAt: new Date(pay.created_at).getTime(),
          description: isCreditNote ? 'Nota de crédito' : `Pago ${pay.method}`,
          entity: getPaymentEntity(pay),
          amount: getPaymentAmountInARS(pay),
          type: payType
        };
      }),
      ...expenses.map(exp => ({
        id: exp.id,
        date: exp.date,
        timestamp: new Date(exp.date).getTime(),
        createdAt: exp.created_at ? new Date(exp.created_at).getTime() : 0,
        description: exp.description,
        entity: exp.supplier_id
          ? suppliers.find(s => s.id === exp.supplier_id)?.name || 'Proveedor desconocido'
          : 'Sin proveedor',
        amount: getExpenseAmountWithoutVAT(exp),
        type: 'expense' as const
      }))
    ];

    return all
      .filter(t => {
        const d = new Date(t.date);
        return d >= periodStartDate && d <= periodEndDate;
      })
      .sort((a, b) => {
        if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
        return (b.createdAt ?? 0) - (a.createdAt ?? 0);
      });
  }, [invoices, payments, expenses, clients, suppliers, periodStartDate, periodEndDate]);

  const recentTransactions = useMemo(() => {
    const start = (transactionsPage - 1) * TRANSACTIONS_PER_PAGE;
    return recentTransactionsAll.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [recentTransactionsAll, transactionsPage]);

  const totalTransactionsPages = Math.ceil(recentTransactionsAll.length / TRANSACTIONS_PER_PAGE);

  
  // Prepare data for sales by salesperson
  const salesBySalesperson = useMemo(() => {
    const salesData: Record<string, {
      invoices: number; 
      productions: number;
      samples: number;
      express: number;
      expressSamples: number;
      total: number;
      commission: number;
    }> = {
      'Mateo': {
        invoices: 0,
        productions: 0,
        samples: 0,
        express: 0,
        expressSamples: 0,
        total: 0,
        commission: 0
      },
      'Male': {
        invoices: 0,
        productions: 0,
        samples: 0,
        express: 0,
        expressSamples: 0,
        total: 0,
        commission: 0
      }
    };
    
    // Add invoice sales by salesperson
    periodInvoices.forEach(invoice => {
      if (invoice.salesperson && salesData[invoice.salesperson]) {
        salesData[invoice.salesperson].invoices += getInvoiceAmountWithoutVAT(invoice);
        salesData[invoice.salesperson].total += getInvoiceAmountWithoutVAT(invoice);
      }
    });
    
    // Add express sales by salesperson
periodExpress.forEach(o => {
  if (o.salesperson && salesData[o.salesperson]) {
    const amt = getExpressAmount(o);
    salesData[o.salesperson].express += amt;
    salesData[o.salesperson].total   += amt;
  }
});

    
    // Calculate commission (2%)
    Object.keys(salesData).forEach(salesperson => {
      salesData[salesperson].commission = salesData[salesperson].total * 0.02;
    });
    
    return salesData;
  }, [periodInvoices, periodExpress]);
  
  // Chart options for sales by client (without legend)
  const salesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend for sales chart
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#333',
        borderColor: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            return `${context.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => formatCurrency(value),
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      },
      x: {
        ticks: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          display: false
        }
      }
    },
    elements: {
      bar: {
        borderRadius: 4
      }
    }
  };
  
  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        },
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#333',
        borderColor: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = Math.round(context.raw * 100 / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    },
    cutout: '50%',
    radius: '90%'
  };
  
  // Refresh data
  const handleRefresh = () => {
    setIsLoading(true);
    
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToPrevMonth}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
            title="Mes anterior"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight min-w-[12rem] text-center">
            {periodLabel}
          </h2>
          <button
            type="button"
            onClick={goToNextMonth}
            disabled={!canGoNextMonth}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
            title="Mes siguiente"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            icon={<TrendingUp className="h-4 w-4" />}
            onClick={() => navigate('/analitica')}
            className="h-10"
          >
            Analítica
          </Button>
          <Select
            label=""
            value={periodMode}
            onChange={handlePeriodModeChange}
            options={[
              { value: 'month', label: 'Por mes' },
              { value: 'quarter', label: 'Este trimestre' },
              { value: 'year', label: 'Este año' },
              { value: 'custom', label: 'Personalizado' },
            ]}
          />
        </div>
      </div>
      
      {periodMode === 'custom' && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Fecha Inicial"
                type="date"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                label="Fecha Final"
                type="date"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Sales KPI */}
        <button
          type="button"
          onClick={() => setIsSalesDetailModalOpen(true)}
          className="w-full text-left bg-gradient-to-br from-white to-indigo-50/60 dark:from-gray-900 dark:to-indigo-900/30 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 p-4 transition-all duration-300 group hover:ring-2 hover:ring-indigo-200 dark:hover:ring-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">
                  Ventas del periodo
                </h3>
                <Eye className="h-3 w-3 text-indigo-500 dark:text-indigo-400 flex-shrink-0 ml-2 opacity-70" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">
                {formatCurrency(detailedSalesData.invoiceSales)}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {invoiceCount} facturas
              </p>
              <div className="flex items-center text-xs">
                <span className={`font-medium ${
                  salesChangePercentage >= 0 
                    ? 'text-emerald-600 dark:text-emerald-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {salesChangePercentage >= 0 ? '+' : ''}{salesChangePercentage}%
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  vs. anterior
                </span>
              </div>
            </div>
            <div className="ml-3 p-2 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg border border-indigo-200 dark:border-indigo-800 flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </button>

        {/* Purchases KPI */}
        <button
          type="button"
          onClick={() => setIsPurchasesDetailModalOpen(true)}
          className="w-full text-left bg-gradient-to-br from-white to-orange-50/60 dark:from-gray-900 dark:to-orange-900/30 rounded-2xl border border-orange-100 dark:border-orange-900/50 p-4 transition-all duration-300 group hover:ring-2 hover:ring-orange-200 dark:hover:ring-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">
                  Compras del periodo
                </h3>
                <Eye className="h-3 w-3 text-orange-500 dark:text-orange-400 flex-shrink-0 ml-2 opacity-70" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">
                {formatCurrency(totalPurchases)}
              </div>
              <div className="flex items-center text-xs">
                <span className={`font-medium ${
                  purchasesChangePercentage >= 0 
                    ? 'text-emerald-600 dark:text-emerald-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {purchasesChangePercentage >= 0 ? '+' : ''}{purchasesChangePercentage}%
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  vs. anterior
                </span>
              </div>
            </div>
            <div className="ml-3 p-2 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-lg border border-orange-200 dark:border-orange-800 flex-shrink-0">
              <ShoppingCart className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </button>

        {/* Receivables KPI */}
        <button
          type="button"
          onClick={() => setIsReceivablesDetailModalOpen(true)}
          className="w-full text-left bg-gradient-to-br from-white to-emerald-50/60 dark:from-gray-900 dark:to-emerald-900/30 rounded-2xl border border-emerald-100 dark:border-emerald-900/50 p-4 transition-all duration-300 group hover:ring-2 hover:ring-emerald-200 dark:hover:ring-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">
                  Cuentas por cobrar
                </h3>
                <Eye className="h-3 w-3 text-emerald-500 dark:text-emerald-400 flex-shrink-0 ml-2 opacity-70" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">
                {formatCurrency(totalReceivable)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Saldo pendiente
              </div>
              <div className="pt-2 border-t border-emerald-100/80 dark:border-emerald-800/50">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    Saldo vencido
                  </span>
                  <span className={`text-sm font-semibold tabular-nums ${
                    totalOverdue > 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {formatCurrency(totalOverdue)}
                  </span>
                </div>
              </div>
            </div>
            <div className="ml-3 p-2 bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800 flex-shrink-0">
              <ArrowUpRight className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </button>

        {/* Margen Bruto % */}
        <div className="bg-gradient-to-br from-white to-amber-50/60 dark:from-gray-900 dark:to-amber-900/30 rounded-2xl border border-amber-100 dark:border-amber-900/50 p-4 transition-all duration-300 ">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">Margen bruto</h3>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 leading-tight">
                {grossMarginPercentage}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formatCurrency(grossMargin)} total
              </div>
            </div>
            <div className="ml-3 p-2 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-lg border border-amber-200 dark:border-amber-800 flex-shrink-0">
              <BarChart2 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* 6 métricas (3 cols x 2 filas) — diseño unificado con KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Satisfacción */}
        <div className="bg-gradient-to-br from-white to-emerald-50/50 dark:from-gray-900 dark:to-emerald-900/20 rounded-2xl border border-emerald-100 dark:border-emerald-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Satisfacción</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 rounded-lg border border-emerald-200/80 dark:border-emerald-800 flex-shrink-0">
              <Smile className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{satisfactionIndex}%</p>
          <div className="h-2 bg-gray-200/80 dark:bg-gray-700 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${Math.min(satisfactionIndex, 100)}%` }} />
          </div>
          <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">Índice 1-5 (encuestas)</p>
        </div>

        {/* Reclamos abiertos */}
        <div className="bg-gradient-to-br from-white to-amber-50/50 dark:from-gray-900 dark:to-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Reclamos abiertos</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-lg border border-amber-200/80 dark:border-amber-800 flex-shrink-0">
              <MessageSquareWarning className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{openComplaintsCount}</p>
          <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">Pendientes</p>
        </div>

        {/* Factura promedio */}
        <div className="bg-gradient-to-br from-white to-indigo-50/50 dark:from-gray-900 dark:to-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Factura promedio</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg border border-indigo-200/80 dark:border-indigo-800 flex-shrink-0">
              <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(averageInvoiceValue)}</p>
          <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">{invoiceCount} facturas</p>
        </div>

        {/* Cumplimiento SLA 60 días - promedio prod + muestras */}
        <div className="bg-gradient-to-br from-white to-sky-50/50 dark:from-gray-900 dark:to-sky-900/20 rounded-2xl border border-sky-100 dark:border-sky-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Cumplimiento SLA 60 días</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-sky-100 to-cyan-100 dark:from-sky-900/30 dark:to-cyan-900/30 rounded-lg border border-sky-200/80 dark:border-sky-800 flex-shrink-0">
              <CheckCircle className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            </div>
          </div>
          <SLAGauge
            value={slaCompliance60d.total > 0 ? slaCompliance60d.percentage : 0}
            onTime={slaCompliance60d.onTime}
            late={slaCompliance60d.late}
            title=""
            icon={null}
          />
          <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            Producciones: {slaCompliance60d.production.total > 0 ? `${slaCompliance60d.production.percentage}%` : '-'} •
            Muestras: {slaCompliance60d.sample.total > 0 ? `${slaCompliance60d.sample.percentage}%` : '-'}
          </p>
        </div>

        {/* Operaciones del período */}
        <div className="bg-gradient-to-br from-white to-violet-50/50 dark:from-gray-900 dark:to-violet-900/20 rounded-2xl border border-violet-100 dark:border-violet-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Operaciones del período</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 rounded-lg border border-violet-200/80 dark:border-violet-800 flex-shrink-0">
              <Package className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{periodOperationsCount}</p>
          <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">Prod. + muestras + express</p>
        </div>

        {/* Proveedores activos */}
        <div className="bg-gradient-to-br from-white to-violet-50/50 dark:from-gray-900 dark:to-violet-900/20 rounded-2xl border border-violet-100 dark:border-violet-900/50 p-4 transition-all duration-300">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate flex-1">Proveedores activos</h3>
            <div className="ml-2 p-1.5 bg-gradient-to-br from-violet-100 to-fuchsia-100 dark:from-violet-900/30 dark:to-fuchsia-900/30 rounded-lg border border-violet-200/80 dark:border-violet-800 flex-shrink-0">
              <Building2 className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{uniqueSuppliersCount}</p>
          <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">Con actividad en el período</p>
        </div>
      </div>

      <PaymentReceiptsCard />
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Ventas por cliente" />
          <CardContent>
            <div className="h-64">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                </div>
              ) : salesByClientData.labels.length > 0 ? (
                <Bar options={salesChartOptions} data={salesByClientData} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500 dark:text-gray-400">No hay datos disponibles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title="Compras por proveedor" />
          <CardContent>
            <div className="h-64">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                </div>
              ) : purchasesBySupplierData.labels.length > 0 ? (
                <Pie options={pieChartOptions} data={purchasesBySupplierData} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500 dark:text-gray-400">No hay datos disponibles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Top Cuentas por Cobrar */}
      <Card>
        <CardHeader title="Top Cuentas por Cobrar" />
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Saldo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {topAccountsReceivable.length > 0 ? (
                    topAccountsReceivable.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {client.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                            {formatCurrency(client.balanceInARS)}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">(ARS)</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No hay cuentas por cobrar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      
      {/* Salesperson Commissions
      <Card>
        <CardHeader title="Comisiones por Vendedor" />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Facturas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Express
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Ventas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Comisión (2%)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {Object.entries(salesBySalesperson).map(([salesperson, data]) => (
                  <tr key={salesperson} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {salesperson}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(data.invoices)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(data.express)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(data.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(data.commission)}
                    </td>
                  </tr>
                ))}
                {Object.keys(salesBySalesperson).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay datos de comisiones para el período seleccionado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>*/}
      
      {/* Recent Transactions */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Transacciones del período</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{format(periodStartDate, 'dd/MM/yyyy', { locale: es })} - {format(periodEndDate, 'dd/MM/yyyy', { locale: es })}</p>
        </div>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entidad
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Monto
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatArgentinaDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {transaction.entity}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      transaction.type === 'income' || transaction.type === 'payment_received'
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {(transaction.type === 'income' || transaction.type === 'payment_received') ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))}
                {recentTransactions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay transacciones en el período seleccionado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalTransactionsPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Mostrando {(transactionsPage - 1) * TRANSACTIONS_PER_PAGE + 1} - {Math.min(transactionsPage * TRANSACTIONS_PER_PAGE, recentTransactionsAll.length)} de {recentTransactionsAll.length}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTransactionsPage(p => Math.max(1, p - 1))}
                    disabled={transactionsPage <= 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTransactionsPage(p => Math.min(totalTransactionsPages, p + 1))}
                    disabled={transactionsPage >= totalTransactionsPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Sales Detail Modal */}
      <SalesDetailModal
        isOpen={isSalesDetailModalOpen}
        onClose={() => setIsSalesDetailModalOpen(false)}
        salesData={detailedSalesData}
        period={periodLabel}
        periodInvoices={periodInvoices}
        periodProductions={periodProductions}
        periodSamples={periodSamples}
        periodExpress={periodExpress}
        periodExpressSamples={periodExpressSamples}
        clients={clients}
      />
      
      {/* Purchases Detail Modal */}
      <PurchasesDetailModal
        isOpen={isPurchasesDetailModalOpen}
        onClose={() => setIsPurchasesDetailModalOpen(false)}
        purchasesData={detailedPurchasesData}
        period={periodLabel}
        periodSupplierInvoices={periodSupplierInvoices}
        periodExpenses={periodExpenses}
        suppliers={suppliers}
        periodProductionTasks={periodProductionTasks}
        periodSampleTasks={periodSampleTasks}
        productions={productions}
        samples={samples}
      />
      
      {/* Receivables Detail Modal */}
      <ReceivablesDetailModal
        isOpen={isReceivablesDetailModalOpen}
        onClose={() => setIsReceivablesDetailModalOpen(false)}
        clients={clients}
        periodInvoices={periodInvoices}
        periodProductions={periodProductions}
        periodSamples={periodSamples}
        periodExpress={periodExpress}
        periodExpressSamples={periodExpressSamples}
      />
      
    </div>
  );
};

export default Dashboard;